import React, { useCallback, useEffect, useState } from 'react'
import { StyleSheet } from 'react-native'
import { DNABox, DNAButton, DNADivider, DNAText, Iffy } from '@alucio/lux-ui'
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors'
import { DNAChip } from 'src/components/DNA'
import { DocumentORM } from 'src/types/types'
import { useLocation } from 'src/router'
import ROUTES from 'src/router/routeDef'
import { useDNADocumentFilters } from './DNADocumentFiltersProvider'
import { useAppSettings } from 'src/state/context/AppSettings'

const S = StyleSheet.create({
  parentContainer: {
    minHeight: 36,
  },
  filterText: {
    marginHorizontal: 4,
  },
  chipContainer: {
    minWidth: 0,
    marginLeft: 8,
    paddingVertical: 2,
  },
  chip: {
    margin: 2,
  },
  buttonGroupContainer: {
    paddingHorizontal: 16,
  },
  buttonBackground: {
    backgroundColor: colors['color-white'],
    borderRadius: 4,
  },
})

interface DocumentFilterChipsProps {
  documents: DocumentORM[];
  onClearFilters?: () => void;
  isLoading?: boolean
  onMinimumWidthChange?: (width: number) => void;
  compact?: boolean;
}

export const DNADocumentFiltersChips = React.memo((props: DocumentFilterChipsProps) => {
  const { documents, isLoading, onClearFilters, onMinimumWidthChange, compact } = props;
  const [labelWidth, setLabelWidth] = useState(0);
  const [buttonsWidth, setButtonsWidth] = useState(0);
  const [chipWidths, setChipWidths] = useState<Record<string, number>>({});
  const {
    activeFilters,
    toggleFilter,
    resetToDefaultFilters,
    clearFilters,
    unpublishedToggle,
    applyDefaultFiltersToInProgress,
    filterEntries,
    setActiveFilters,
  } = useDNADocumentFilters();
  const { pathname } = useLocation();
  const lowerCasedPathName = pathname.toLowerCase();
  const isPublisherSearch = lowerCasedPathName === ROUTES.SEARCH_RESULTS_PUBLISHER.PATH;
  const isPublisher = [ROUTES.PUBLISHER_DASHBOARD.PATH].includes(lowerCasedPathName) || isPublisherSearch;
  const { deviceMode } = useAppSettings()

  const handleClearFilter = useCallback(() => {
    clearFilters()
    onClearFilters?.()
  }, [clearFilters, onClearFilters])

  const handleResetToDefaultFilters = useCallback(() => {
    if (unpublishedToggle && applyDefaultFiltersToInProgress) {
      setActiveFilters(() => filterEntries.filter(entry => entry.default && entry.fieldId !== 'Status'))
      return
    }

    resetToDefaultFilters()
  }, [
    applyDefaultFiltersToInProgress,
    filterEntries,
    resetToDefaultFilters,
    setActiveFilters,
    unpublishedToggle,
  ])

  useEffect(() => {
    if (!labelWidth || activeFilters.some(entry => chipWidths[entry.fieldValue] === undefined)) return;

    // Keep room for the widest chip; the others can wrap before the toolbar does.
    const widestChip = Math.max(0, ...activeFilters.map(entry => chipWidths[entry.fieldValue]));
    const chipSpacing = activeFilters.length ? 4 : 0;
    onMinimumWidthChange?.(Math.ceil(labelWidth + buttonsWidth + widestChip + 8 + chipSpacing));
  }, [activeFilters, labelWidth, buttonsWidth, chipWidths, onMinimumWidthChange]);

  const filterModifierButtonSize = deviceMode === 'desktop' ? 'sm' : 'md'

  if (compact) {
    return (
      <DNABox fill appearance="row" wrap="start" alignY="center" spacing="xs" style={{ minWidth: 0 }}>
        {!isLoading && <DNAText testID="filter-count-label">{documents.length} item(s)</DNAText>}
        {activeFilters.map(entry => (
          <DNAChip.Beacon
            key={`${entry.fieldId}-${entry.valueId}`}
            testID="filter-chip"
            variant="USER_FILTER_MSL"
            onPress={() => toggleFilter(entry.fieldId, entry.valueId)}
            style={S.chip}
            status="secondary"
            appearance="subtle"
          >
            {entry.fieldValue}
          </DNAChip.Beacon>
        ))}
        {(!unpublishedToggle || applyDefaultFiltersToInProgress) && (
          <DNAButton
            testID="filter-chips-reset-button"
            appearance="outline"
            status="tertiary"
            size={filterModifierButtonSize}
            onPress={handleResetToDefaultFilters}
          >
            Set to default
          </DNAButton>
        )}
        {!!activeFilters.length && (
          <DNAButton
            testID="filter-chips-clear-button"
            appearance="outline"
            status="tertiary"
            size={filterModifierButtonSize}
            onPress={handleClearFilter}
          >
            Clear
          </DNAButton>
        )}
      </DNABox>
    );
  }

  return (
    <DNABox fill alignY="center" style={S.parentContainer}>
      <DNABox fill alignY="center" alignX="start">
        <DNABox alignY="center" spacing="sm" onLayout={event => setLabelWidth(event.nativeEvent.layout.width)}>
          <Iffy is={!isLoading}>
            <DNAText testID="filter-count-label">
              {documents.length} item(s)
            </DNAText>
          </Iffy>
          <DNADivider vertical style={S.filterText} />
          <DNAText>Filters:</DNAText>
        </DNABox>
        {/* FILTER CHIPS */}
        <DNABox shrink wrap="start" alignY="start" spacing="xs" style={S.chipContainer}>
          {
            activeFilters.map(entry => (
              <DNABox
                key={entry.fieldValue}
                onLayout={event => {
                  const { width } = event.nativeEvent.layout;
                  setChipWidths(previous => previous[entry.fieldValue] === width
                    ? previous
                    : { ...previous, [entry.fieldValue]: width });
                }}
              >
                <DNAChip.Beacon
                  testID="filter-chip"
                  variant={isPublisher ? 'USER_FILTER_PUBLISHER' : 'USER_FILTER_MSL'}
                  onPress={() => { toggleFilter(entry.fieldId, entry.valueId) }}
                  style={S.chip}
                  status="secondary"
                  appearance="subtle"
                  children={entry.fieldValue}
                />
              </DNABox>
            ))
          }
        </DNABox>
        {/* SET TO DEFAULT AND CLEAR BUTTONS */}
        <DNABox alignY="center" onLayout={event => setButtonsWidth(event.nativeEvent.layout.width)}>
          <DNABox spacing="sm" style={S.buttonGroupContainer}>
            {(!unpublishedToggle || applyDefaultFiltersToInProgress) &&
              <DNABox style={S.buttonBackground}>
                <DNAButton
                  testID="filter-chips-reset-button"
                  appearance="outline"
                  status="tertiary"
                  size={filterModifierButtonSize}
                  onPress={handleResetToDefaultFilters}
                  children="Set to default"
                />
              </DNABox>
            }
            <Iffy is={activeFilters.length}>
              <DNABox style={S.buttonBackground}>
                <DNAButton
                  testID="filter-chips-clear-button"
                  appearance="outline"
                  status="tertiary"
                  size={filterModifierButtonSize}
                  onPress={handleClearFilter}
                  children="Clear"
                />
              </DNABox>
            </Iffy>
          </DNABox>
        </DNABox>
      </DNABox>
    </DNABox>
  )
});
